package com.dotdrives.web.pages.nextsteps

import com.dotdrives.controller.AutomationRuleDraft
import com.dotdrives.web.ui.DotButton
import react.FC
import react.Props
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.textarea
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.text

external interface RuleBuilderDialogProps : Props {
    var draft: AutomationRuleDraft
    var previewCount: Int?
    var previewComplete: Boolean
    var previewLoading: Boolean
    var saving: Boolean
    var builderError: String?
    var onSetName: (String) -> Unit
    var onSetMinAmountCents: (Long?) -> Unit
    var onSetDescriptionTemplate: (String) -> Unit
    var onSetDueInDays: (Int) -> Unit
    var onCancel: () -> Unit
    var onSave: () -> Unit
}

/** "New automation rule ⚡" -- the WHEN … THEN … rule builder with its would-have-fired
 * preview (`portal-next-steps.dc.html`, Rule Builder). The trigger and action are fixed to
 * "a gift is received" / "create a step for the giver's steward" -- the only verb-dispatched
 * trigger and the only well-defined assignee value the schema names (docs/schema.md) --
 * widening to other event types is future work, not a gap in this pass. Rendered as static
 * summary text (`.ns-builder-summary`), not a select-shaped `cursor: pointer` box: there is
 * nothing behind either row to pick, so styling them as pickers would be a false affordance,
 * the same reasoning `PipelineHealthPanel`'s "Add as step" text uses. No control here can ever
 * hold a money token in the template -- see `com.dotdrives.model.containsMoneyToken` and the
 * controller's own gate, both enforced (and tested) above this view. */
val RuleBuilderDialog = FC<RuleBuilderDialogProps> { props ->
    val draft = props.draft

    div {
        className = ClassName("ns-dialog-backdrop")
        role = "presentation".unsafeCast<AriaRole>()
        onClick = { props.onCancel() }

        div {
            className = ClassName("ns-dialog")
            role = "dialog".unsafeCast<AriaRole>()
            ariaModal = true
            ariaLabel = "New automation rule"
            onClick = { it.stopPropagation() }

            div { className = ClassName("ns-dialog-title"); +"New automation rule ⚡" }
            div { className = ClassName("ns-dialog-sub"); +"When something happens, create the next step automatically." }

            input {
                className = ClassName("ns-subject-search ns-rule-name-input")
                type = InputType.text
                value = draft.name
                placeholder = "Name this rule — e.g. \"Big gift thank-you\""
                ariaLabel = "Rule name"
                onChange = { props.onSetName(it.currentTarget.value) }
            }

            div { className = ClassName("ns-builder-label"); +"WHEN" }
            div { className = ClassName("ns-builder-summary"); +"💚 A gift is received" }
            div {
                className = ClassName("ns-builder-condition-row")
                span {
                    className = ClassName("ns-builder-condition")
                    +"over $"
                    input {
                        className = ClassName("ns-builder-amount-input")
                        type = InputType.text
                        value = formatMinAmount(draft.minAmountCents)
                        placeholder = "0"
                        ariaLabel = "Minimum gift amount in dollars"
                        onChange = { props.onSetMinAmountCents(parseMinAmountCents(it.currentTarget.value)) }
                    }
                }
            }

            div { className = ClassName("ns-builder-label"); +"THEN" }
            div { className = ClassName("ns-builder-summary"); +"✓ Create a step for the giver's steward" }
            textarea {
                className = ClassName("ns-builder-template")
                value = draft.descriptionTemplate
                placeholder = "What should the step say?"
                ariaLabel = "Step description template"
                onChange = { props.onSetDescriptionTemplate(it.currentTarget.value) }
            }
            props.builderError?.let { error ->
                div { className = ClassName("ns-builder-error"); +error }
            }
            div {
                className = ClassName("ns-builder-due-row")
                +"due in "
                input {
                    className = ClassName("ns-builder-due-input")
                    type = InputType.text
                    value = draft.dueInDays.toString()
                    ariaLabel = "Days until due"
                    onChange = { event -> event.currentTarget.value.toIntOrNull()?.let(props.onSetDueInDays) }
                }
                +" days"
            }

            div {
                className = ClassName("ns-builder-preview")
                +fireCountLabel(props.previewCount, props.previewLoading, props.previewComplete)
            }

            div {
                className = ClassName("ns-dialog-actions")
                DotButton {
                    text = "Cancel"
                    variant = com.dotdrives.design.DotButtonVariant.Secondary
                    onClick = props.onCancel
                    enabled = !props.saving
                }
                DotButton {
                    text = "⚡ Turn on rule"
                    onClick = props.onSave
                    loading = props.saving
                }
            }
        }
    }
}
